import * as Sentry from '@sentry/react';

/**
 * Recovery from an API token the backend no longer accepts.
 *
 * The application authenticates every API call with the DRF token that
 * `partaj_context` embeds in the page at render time. That token used to live forever;
 * it is now revoked when its owner logs out and when an impersonation of them ends, and
 * the tokens minted before those rules existed have to be purged. A tab holding a
 * revoked token gets a 401 on its next call.
 *
 * Nothing handled that. Of the fifty-odd `fetch` calls in the application, five look at
 * the status at all, and only for a 400: a 401 surfaced as `Failed to get /referrals/x/`
 * or as a silently empty screen, indistinguishable from a server error, with no way out
 * but for the user to reload by hand. Reloading is exactly what fixes it — the Django
 * session cookie is untouched, so the page re-renders with a fresh token — so the
 * application should do it itself.
 *
 * This is installed as a single wrapper around `window.fetch` rather than in the data
 * layer on purpose. The calls are spread over forty-two files and several idioms
 * (`fetchOne`, `sendForm`, bare `fetch` in components); patching them one by one would
 * have missed some and would not cover the next one written.
 */

/**
 * Timestamp of the last reload we triggered, kept in `sessionStorage` so it survives the
 * reload it is guarding against.
 */
const RELOAD_MARKER_KEY = 'partaj.expiredTokenReloadAt';

/**
 * How long after a reload a 401 means the reload did not help.
 *
 * Reloading is only a fix if the page comes back with a token the API accepts. If it
 * does not — a dead session that still serves the page, a backend that rejects every
 * token — reloading again would loop forever, taking the browser with it. Past this
 * delay we assume the 401 is a new event and reloading is worth trying again.
 */
const RELOAD_COOLDOWN_MS = 30 * 1000;

const readLastReloadAt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER_KEY)) || 0;
  } catch (error) {
    return 0;
  }
};

/**
 * Record that we are about to reload. Returns false if we could not.
 *
 * The write is what makes the loop guard work, so a browser that refuses it (private
 * modes, storage disabled) must not get the reload either: an unguarded reload loop is
 * worse than an unhandled 401.
 */
const rememberReload = (): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Whether a response belongs to our own API.
 *
 * Third-party calls are left alone: Sentry answers 401 to a stale DSN, and that must not
 * reload the user's page.
 */
const isOwnOrigin = (input: RequestInfo | URL): boolean => {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : input.url;

  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch (error) {
    return false;
  }
};

/**
 * React to an API call rejected for lack of a valid credential.
 *
 * Exported for the tests; `installExpiredTokenHandler` is the entry point.
 */
export const handleExpiredToken = (): void => {
  const sinceLastReload = Date.now() - readLastReloadAt();

  if (sinceLastReload < RELOAD_COOLDOWN_MS) {
    Sentry.captureMessage(
      'API returned 401 right after a reload: reloading did not restore a usable token',
      'error',
    );
    return;
  }

  if (!rememberReload()) {
    Sentry.captureMessage(
      'API returned 401 but sessionStorage is unavailable: not reloading, as the ' +
        'loop guard cannot be set',
      'error',
    );
    return;
  }

  window.location.reload();
};

/**
 * Wrap `window.fetch` so that a 401 from our API reloads the page once.
 *
 * The response is returned untouched: callers keep whatever error handling they have,
 * and the reload happens underneath them.
 */
export const installExpiredTokenHandler = (): void => {
  const originalFetch = window.fetch;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await originalFetch(input, init);

    if (response.status === 401 && isOwnOrigin(input)) {
      handleExpiredToken();
    }

    return response;
  };
};
